import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { cn } from "@/lib/utils";
import { DateRangeFilter, buildQuery, filterSelectClass, useDateRange } from "@/components/reports/DateRangeFilter";
import { downloadCsv } from "@/components/reports/ReportWidgets";
import type { ReportUser } from "@/types/report";
import {
  ActivityTab,
  AgeingTab,
  AssignmentsTab,
  ExecTab,
  ForecastTab,
  HeadTab,
  LostTab,
  PipelineTab,
  ProductsTab,
  QuotesTab,
  SourcesTab,
} from "./ReportTabs";
import { TextileTab } from "./TextileTab";

type TabKey =
  | "execs"
  | "heads"
  | "pipeline"
  | "sources"
  | "lost"
  | "ageing"
  | "activity"
  | "quotes"
  | "forecast"
  | "products"
  | "textile"
  | "assignments";

interface TabDef {
  key: TabKey;
  label: string;
  /** Permission needed to see the tab (matches the API guard) */
  permission?: string;
  /** Tab shows current state, so the period filter does not apply */
  noPeriod?: boolean;
  exportType?: string;
}

const TABS: TabDef[] = [
  { key: "execs", label: "Sales Executives", exportType: "exec-performance" },
  { key: "heads", label: "Sales Heads", permission: "report.view_all", exportType: "head-performance" },
  { key: "pipeline", label: "Pipeline", noPeriod: true, exportType: "pipeline" },
  { key: "forecast", label: "Forecast", noPeriod: true, exportType: "forecast" },
  { key: "sources", label: "Lead Sources", exportType: "lead-source" },
  { key: "lost", label: "Lost Analysis", exportType: "lost-deals" },
  { key: "ageing", label: "Lead Ageing", noPeriod: true, exportType: "lead-ageing" },
  { key: "activity", label: "Activity", exportType: "activity" },
  { key: "quotes", label: "Quotes", exportType: "quotes" },
  { key: "products", label: "Products", permission: "report.view_team", exportType: "product-sales" },
  { key: "textile", label: "Textile Sales", exportType: "textile-sales" },
  { key: "assignments", label: "Assignments", permission: "lead.assign", exportType: "assignment-audit" },
];

export function ReportsPage() {
  const { hasPermission, getScope } = useAuth();
  const { error } = useToast();
  const [params, setParams] = useSearchParams();
  const { preset, range, choosePreset, setRange } = useDateRange("this_month");
  const [heads, setHeads] = useState<ReportUser[]>([]);
  const [exporting, setExporting] = useState(false);

  const scope = getScope("report");
  const visibleTabs = useMemo(() => TABS.filter((t) => !t.permission || hasPermission(t.permission)), [hasPermission]);
  const requested = params.get("tab") as TabKey | null;
  const active: TabDef = visibleTabs.find((t) => t.key === requested) ?? visibleTabs[0] ?? (TABS[0] as TabDef);
  const headId = params.get("head") || "";
  const canExport = hasPermission("report.export");

  // Only Super Admin compares several Sales Heads, so only they get the head filter
  useEffect(() => {
    if (scope !== "all") return;
    api
      .get("/api/Report/Users")
      .then((res) => setHeads(unwrapArray<ReportUser>(res).filter((u) => u.role_code === "SALES_HEAD")))
      .catch(() => setHeads([]));
  }, [scope]);

  const setTab = (key: TabKey) => {
    const next = new URLSearchParams(params);
    next.set("tab", key);
    setParams(next, { replace: true });
  };

  const setHead = (id: string) => {
    const next = new URLSearchParams(params);
    if (id) next.set("head", id);
    else next.delete("head");
    setParams(next, { replace: true });
  };

  const handleExport = async () => {
    if (!active.exportType) return;
    setExporting(true);
    try {
      const res = await api.get(`/api/Report/Export/${active.exportType}${buildQuery({ from: range.from, to: range.to, head_id: headId })}`);
      const { filename, csv } = unwrapObject<{ filename: string; csv: string }>(res);
      downloadCsv(filename, csv);
    } catch (err: any) {
      error("Export failed", err.message || "Could not export report");
    } finally {
      setExporting(false);
    }
  };

  const tabProps = { range, headId };
  const description =
    scope === "all"
      ? "Company-wide numbers for every Sales Head and Sales Executive."
      : scope === "team"
        ? "Numbers for you and the Sales Executives in your team."
        : "Your own numbers.";

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-foreground">Reports</h2>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        {canExport && active.exportType && (
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting}>
            {exporting ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
            Export CSV
          </Button>
        )}
      </div>

      <div role="tablist" aria-label="Reports" className="flex gap-1 overflow-x-auto border-b border-border pb-2">
        {visibleTabs.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={t.key === active.key}
            onClick={() => setTab(t.key)}
            className={cn(
              "px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors",
              t.key === active.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-accent"
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-4">
        {!active.noPeriod ? (
          <DateRangeFilter preset={preset} range={range} onPreset={choosePreset} onRange={setRange} />
        ) : (
          <span className="text-xs text-muted-foreground">Shows open deals and leads as of now.</span>
        )}
        {scope === "all" && active.key !== "heads" && heads.length > 0 && (
          <div className="flex items-center gap-2">
            <label htmlFor="report-head" className="text-xs text-muted-foreground">
              Sales Head
            </label>
            <select id="report-head" className={filterSelectClass} value={headId} onChange={(e) => setHead(e.target.value)}>
              <option value="">All teams</option>
              {heads.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {active.key === "execs" && <ExecTab {...tabProps} />}
      {active.key === "heads" && (
        <HeadTab
          {...tabProps}
          onPickHead={(id) => {
            const next = new URLSearchParams(params);
            next.set("tab", "execs");
            next.set("head", id);
            setParams(next, { replace: true });
          }}
        />
      )}
      {active.key === "pipeline" && <PipelineTab {...tabProps} />}
      {active.key === "forecast" && <ForecastTab {...tabProps} />}
      {active.key === "sources" && <SourcesTab {...tabProps} />}
      {active.key === "lost" && <LostTab {...tabProps} />}
      {active.key === "ageing" && <AgeingTab {...tabProps} />}
      {active.key === "activity" && <ActivityTab {...tabProps} />}
      {active.key === "quotes" && <QuotesTab {...tabProps} />}
      {active.key === "products" && <ProductsTab {...tabProps} />}
      {active.key === "textile" && <TextileTab {...tabProps} />}
      {active.key === "assignments" && <AssignmentsTab {...tabProps} />}
    </div>
  );
}
